'use client'

/**
 * Recipient Analytics Hooks
 *
 * React Query hooks for fetching recipient dashboard analytics data.
 * Each hook fetches one section independently, enabling parallel loading
 * and independent Suspense boundaries.
 */
import { useMemo } from 'react'

import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import {
  fetchAwardTimeSeries,
  fetchIdvGroupTimeSeries,
  fetchIdvParent,
  fetchRecipientAwardPortfolio,
  fetchRecipientAwardTypes,
  fetchRecipientCeilingTimeSeries,
  fetchRecipientCompetition,
  fetchRecipientContractSizes,
  fetchRecipientDashboard,
  fetchRecipientGeography,
  fetchRecipientGeographyCities,
  fetchRecipientOutlayTimeSeries,
  fetchRecipientSetAsides,
  fetchRecipientTimeSeries,
} from '@/context/usaspending'

import type { RecipientAnalyticsFilters } from '@/types/usaspending'

/**
 * Analytics data stale time (7 days).
 * Matches ISR revalidation period so dehydrated data is never refetched
 * during the ISR window. Federal spending data updates at most daily.
 */
const ANALYTICS_STALE_TIME = 7 * 24 * 60 * 60 * 1000

/**
 * Cache for 7 days (matches staleTime / ISR revalidation)
 */
const ANALYTICS_GC_TIME = 7 * 24 * 60 * 60 * 1000

function normalizeRecipientAnalyticsFilters(
  filters?: RecipientAnalyticsFilters,
) {
  return {
    awardCategory: filters?.awardCategory,
    popStateCode: filters?.popStateCode,
    awardingAgencyCode: filters?.awardingAgencyCode,
    naicsCode: filters?.naicsCode,
    pscCode: filters?.pscCode,
    typeSetAside: filters?.typeSetAside,
    extentCompeted: filters?.extentCompeted,
  }
}

// =============================================================================
// Query Keys
// =============================================================================

export const recipientAnalyticsKeys = {
  all: ['usaspending', 'recipient', 'analytics'] as const,
  timeSeries: (recipientId: string, granularity?: string) =>
    [
      ...recipientAnalyticsKeys.all,
      'time-series',
      recipientId,
      { granularity },
    ] as const,
  awardTypes: (recipientId: string, fiscalYear?: number) =>
    [
      ...recipientAnalyticsKeys.all,
      'award-types',
      recipientId,
      { fiscalYear },
    ] as const,
  geography: (
    recipientId: string,
    fiscalYear?: number,
    filters?: RecipientAnalyticsFilters,
  ) =>
    [
      ...recipientAnalyticsKeys.all,
      'geography',
      recipientId,
      { fiscalYear, ...normalizeRecipientAnalyticsFilters(filters) },
    ] as const,
  geographyCities: (
    recipientId: string,
    fiscalYear?: number,
    filters?: RecipientAnalyticsFilters,
  ) =>
    [
      ...recipientAnalyticsKeys.all,
      'geography-cities',
      recipientId,
      { fiscalYear, ...normalizeRecipientAnalyticsFilters(filters) },
    ] as const,
  setAsides: (recipientId: string, fiscalYear?: number) =>
    [
      ...recipientAnalyticsKeys.all,
      'set-asides',
      recipientId,
      { fiscalYear },
    ] as const,
  competition: (recipientId: string, fiscalYear?: number) =>
    [
      ...recipientAnalyticsKeys.all,
      'competition',
      recipientId,
      { fiscalYear },
    ] as const,
  contractSizes: (recipientId: string, fiscalYear?: number) =>
    [
      ...recipientAnalyticsKeys.all,
      'contract-sizes',
      recipientId,
      { fiscalYear },
    ] as const,
  dashboard: (recipientId: string, fiscalYear?: number) =>
    [
      ...recipientAnalyticsKeys.all,
      'dashboard',
      recipientId,
      { fiscalYear },
    ] as const,
  awardPortfolio: (recipientId: string) =>
    [...recipientAnalyticsKeys.all, 'award-portfolio', recipientId] as const,
  outlayTimeSeries: (recipientId: string) =>
    [...recipientAnalyticsKeys.all, 'outlay-time-series', recipientId] as const,
  ceilingTimeSeries: (recipientId: string) =>
    [
      ...recipientAnalyticsKeys.all,
      'ceiling-time-series',
      recipientId,
    ] as const,
  idvGroupTimeSeries: (
    recipientId: string,
    parentPiid: string,
    granularity?: string,
  ) =>
    [
      ...recipientAnalyticsKeys.all,
      'idv-group-time-series',
      recipientId,
      parentPiid,
      { granularity },
    ] as const,
  awardTimeSeries: (awardId: string, granularity?: string) =>
    ['usaspending', 'award', 'time-series', awardId, { granularity }] as const,
  idvParent: (awardId: string) =>
    ['usaspending', 'award', 'idv-parent', awardId] as const,
}

// =============================================================================
// Query Options Factory
// =============================================================================

/**
 * Factory hook for recipient analytics query options.
 * Use these for prefetching, SSR, or manual query management.
 *
 * Uses `accessToken ?? ''` instead of `assertToken(accessToken)` so that
 * queryFn never throws when auth is unavailable (e.g. during ISR hydration).
 * An empty token causes `createHeaders` to omit the Authorization header,
 * hitting the backend's `maybe_auth_user_id` path instead.
 */
export function useRecipientAnalyticsQueries() {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''

  return useMemo(
    () => ({
      timeSeries: (recipientId: string, granularity?: string) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.timeSeries(recipientId, granularity),
          queryFn: () =>
            fetchRecipientTimeSeries(token, recipientId, granularity),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      awardTypes: (recipientId: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.awardTypes(recipientId, fiscalYear),
          queryFn: () =>
            fetchRecipientAwardTypes(token, recipientId, fiscalYear),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      geography: (
        recipientId: string,
        fiscalYear?: number,
        filters?: RecipientAnalyticsFilters,
      ) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.geography(
            recipientId,
            fiscalYear,
            filters,
          ),
          queryFn: () =>
            fetchRecipientGeography(
              token,
              recipientId,
              fiscalYear,
              51,
              filters,
            ),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      geographyCities: (
        recipientId: string,
        fiscalYear?: number,
        filters?: RecipientAnalyticsFilters,
      ) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.geographyCities(
            recipientId,
            fiscalYear,
            filters,
          ),
          queryFn: () =>
            fetchRecipientGeographyCities(
              token,
              recipientId,
              fiscalYear,
              250,
              filters,
            ),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      setAsides: (recipientId: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.setAsides(recipientId, fiscalYear),
          queryFn: () =>
            fetchRecipientSetAsides(token, recipientId, fiscalYear),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      competition: (recipientId: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.competition(recipientId, fiscalYear),
          queryFn: () =>
            fetchRecipientCompetition(token, recipientId, fiscalYear),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      contractSizes: (recipientId: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.contractSizes(
            recipientId,
            fiscalYear,
          ),
          queryFn: () =>
            fetchRecipientContractSizes(token, recipientId, fiscalYear),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),

      dashboard: (recipientId: string, fiscalYear?: number) =>
        queryOptions({
          queryKey: recipientAnalyticsKeys.dashboard(recipientId, fiscalYear),
          queryFn: () =>
            fetchRecipientDashboard(token, recipientId, fiscalYear, false),
          staleTime: ANALYTICS_STALE_TIME,
          gcTime: ANALYTICS_GC_TIME,
          refetchOnWindowFocus: false,
        }),
    }),
    [token],
  )
}

// =============================================================================
// Standard Hooks (useQuery)
// =============================================================================

/**
 * Fetch spending time series for a recipient.
 * Shows spending trend by fiscal year (FY2023+).
 */
export function useRecipientTimeSeries(
  recipientId: string,
  granularity?: string,
) {
  const queries = useRecipientAnalyticsQueries()
  return useQuery(queries.timeSeries(recipientId, granularity))
}

/**
 * Fetch award type breakdown for a recipient.
 * Shows distribution across contract, grant, IDV, loan, etc.
 */
export function useRecipientAwardTypes(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useQuery(queries.awardTypes(recipientId, fiscalYear))
}

/**
 * Fetch geographic distribution for a recipient.
 * Shows spending by place of performance state.
 */
export function useRecipientGeography(
  recipientId: string,
  fiscalYear?: number,
  filters?: RecipientAnalyticsFilters,
) {
  const queries = useRecipientAnalyticsQueries()
  return useQuery(queries.geography(recipientId, fiscalYear, filters))
}

export function useRecipientGeographyCities(
  recipientId: string,
  fiscalYear?: number,
  filters?: RecipientAnalyticsFilters,
) {
  const queries = useRecipientAnalyticsQueries()
  return useQuery(queries.geographyCities(recipientId, fiscalYear, filters))
}

/**
 * Fetch set-aside breakdown for a recipient.
 * Shows distribution by set-aside program type.
 */
export function useRecipientSetAsides(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useQuery(queries.setAsides(recipientId, fiscalYear))
}

/**
 * Fetch competition breakdown for a recipient.
 * Shows distribution by extent competed.
 */
export function useRecipientCompetition(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useQuery(queries.competition(recipientId, fiscalYear))
}

/**
 * Fetch contract size histogram for a recipient.
 * Shows distribution of award amounts in logarithmic buckets.
 */
export function useRecipientContractSizes(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useQuery(queries.contractSizes(recipientId, fiscalYear))
}

// =============================================================================
// Suspense Hooks (useSuspenseQuery)
// =============================================================================

/**
 * Suspense-compatible time series hook.
 * Use within a Suspense boundary for streaming loading.
 */
export function useSuspenseRecipientTimeSeries(
  recipientId: string,
  granularity?: string,
) {
  const queries = useRecipientAnalyticsQueries()
  return useSuspenseQuery(queries.timeSeries(recipientId, granularity))
}

/**
 * Suspense-compatible award types hook.
 */
export function useSuspenseRecipientAwardTypes(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useSuspenseQuery(queries.awardTypes(recipientId, fiscalYear))
}

/**
 * Suspense-compatible geography hook.
 */
export function useSuspenseRecipientGeography(
  recipientId: string,
  fiscalYear?: number,
  filters?: RecipientAnalyticsFilters,
) {
  const queries = useRecipientAnalyticsQueries()
  return useSuspenseQuery(queries.geography(recipientId, fiscalYear, filters))
}

/**
 * Suspense-compatible set-asides hook.
 */
export function useSuspenseRecipientSetAsides(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useSuspenseQuery(queries.setAsides(recipientId, fiscalYear))
}

/**
 * Suspense-compatible competition hook.
 */
export function useSuspenseRecipientCompetition(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useSuspenseQuery(queries.competition(recipientId, fiscalYear))
}

/**
 * Suspense-compatible contract sizes hook.
 */
export function useSuspenseRecipientContractSizes(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useSuspenseQuery(queries.contractSizes(recipientId, fiscalYear))
}

// =============================================================================
// Dashboard Hook (Batched)
// =============================================================================

/**
 * Suspense-compatible batched dashboard hook.
 * All section components calling this with the same args share a single HTTP request
 * via React Query deduplication.
 */
export function useSuspenseRecipientDashboard(
  recipientId: string,
  fiscalYear?: number,
) {
  const queries = useRecipientAnalyticsQueries()
  return useSuspenseQuery(queries.dashboard(recipientId, fiscalYear))
}

// Award Cash Flow Hooks
// =============================================================================

/**
 * Fetch the full award portfolio for a recipient (all awards with ceiling/obligation/outlay).
 * Uses useQuery (not Suspense) so the chart can render obligation data immediately
 * and add ceiling/outlay lines when portfolio data arrives.
 */
export function useRecipientAwardPortfolio(
  recipientId: string,
  options?: { enabled?: boolean },
) {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''
  return useQuery({
    queryKey: recipientAnalyticsKeys.awardPortfolio(recipientId),
    queryFn: () => fetchRecipientAwardPortfolio(token, recipientId),
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Fetch time series for a single award.
 * Enabled only when awardId is provided (non-null).
 */
export function useAwardTimeSeries(
  awardId: string | null,
  granularity?: string,
) {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''
  return useQuery({
    queryKey: recipientAnalyticsKeys.awardTimeSeries(
      awardId ?? '',
      granularity,
    ),
    queryFn: () => fetchAwardTimeSeries(token, awardId!, granularity),
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
    refetchOnWindowFocus: false,
    enabled: !!awardId,
  })
}

/**
 * Fetch the IDV parent for a given award (if it's a child order).
 * Enabled only when awardId is provided (non-null).
 */
export function useIdvParent(awardId: string | null) {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''
  return useQuery({
    queryKey: recipientAnalyticsKeys.idvParent(awardId ?? ''),
    queryFn: () => fetchIdvParent(token, awardId!),
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
    refetchOnWindowFocus: false,
    enabled: !!awardId,
  })
}

/**
 * Fetch monthly outlay time series from File C (FABA) for a recipient.
 * Non-suspense hook — loads progressively alongside portfolio data.
 */
export function useRecipientOutlayTimeSeries(recipientId: string) {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''
  return useQuery({
    queryKey: recipientAnalyticsKeys.outlayTimeSeries(recipientId),
    queryFn: () => fetchRecipientOutlayTimeSeries(token, recipientId),
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch monthly ceiling time series (by date_signed) for a recipient.
 * Non-suspense hook — loads progressively alongside portfolio data.
 */
export function useRecipientCeilingTimeSeries(recipientId: string) {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''
  return useQuery({
    queryKey: recipientAnalyticsKeys.ceilingTimeSeries(recipientId),
    queryFn: () => fetchRecipientCeilingTimeSeries(token, recipientId),
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Fetch per-child obligation time series for an IDV group.
 * Non-suspense hook, enabled only when parentPiid is non-null.
 */
export function useIdvGroupTimeSeries(
  recipientId: string,
  parentPiid: string | null,
  granularity?: string,
) {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''
  return useQuery({
    queryKey: recipientAnalyticsKeys.idvGroupTimeSeries(
      recipientId,
      parentPiid ?? '',
      granularity,
    ),
    queryFn: () =>
      fetchIdvGroupTimeSeries(token, recipientId, parentPiid!, granularity),
    staleTime: ANALYTICS_STALE_TIME,
    gcTime: ANALYTICS_GC_TIME,
    refetchOnWindowFocus: false,
    enabled: !!parentPiid,
  })
}
